"use client";
import type { SurveyQuestion, ContentVideoOptions } from "@/types/survey";

interface ContentVideoBlockProps {
  question: SurveyQuestion;
}

function getVideoEmbedUrl(url: string): string | null {
  if (!url) return null;
  try {
    // YouTube
    const ytMatch = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    );
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    // Vimeo
    const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  } catch {
    /* ignore */
  }
  return null;
}

export default function ContentVideoBlock({ question }: ContentVideoBlockProps) {
  const opts = (question.options || {}) as ContentVideoOptions;
  const videoUrl = opts.videoUrl;

  if (!videoUrl) return null;

  const embedUrl = getVideoEmbedUrl(videoUrl);
  if (!embedUrl) {
    return (
      <div className="mb-6" style={{ textAlign: "center", color: "var(--color-alias-neutral-dark)" }}>
        <p>Invalid video URL. Please provide a YouTube or Vimeo link.</p>
      </div>
    );
  }

  return (
    <div className="mb-6" style={{ width: "100%", maxWidth: opts.maxWidth || "100%" }}>
      <div
        style={{
          position: "relative",
          width: "100%",
          paddingBottom: "56.25%" /* 16:9 aspect ratio */,
          height: 0,
          overflow: "hidden",
          borderRadius: 8,
        }}
      >
        <iframe
          src={embedUrl}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: "none",
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={question.text || "Embedded video"}
        />
      </div>
    </div>
  );
}
